import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications"; // UPDATED IMPORTS
import { useDebounce } from "../hook/useDebounce";

const LocationsSubListPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  
  const debouncedSearch = useDebounce(search, 500);

  // Wrapped in useCallback for performance and consistency
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/sub-locations", {
        params: {
          search: debouncedSearch,
          page,
          limit,
          parent_id: state?.filterBusinessId,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
        },
      });

      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch {
      showToast("error", "Failed to load sub locations"); // UPDATED
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, state?.filterBusinessId, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {state?.filterBusinessName && (
        <div className="mb-12 px-10">
          <span
            className="badge bg-primary-100 text-primary-600 px-12 py-8 radius-8 pointer d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/locations-sub", { replace: true })}
          >
            Business: {state.filterBusinessName} 
            <i className="ri-close-line"></i>
          </span>
        </div>
      )}

      <DataTable
        title="Sub Locations"
        data={data}
        loading={loading}
        searchTerm={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => {
          setLimit(v);
          setPage(1);
        }}
        
        showCheckbox={false}

        sortConfig={sortConfig}
        onSortChange={(config) => {
          setSortConfig(config);
          setPage(1);
        }}

        headerActions={[
          {
            label: "Add Unit",
            icon: "ri-add-line",
            className: "btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2",
            onClick: () => navigate("/locations-sub/new"),
          },
        ]}

        columns={[
          { 
            key: "name", 
            label: "Unit Name", 
            className: "text-md fw-semibold text-primary-light",
            sortable: true 
          },
          { 
            key: "type", 
            label: "Type", 
            sortable: true,
            render: (row) => (
              <span className="bg-info-focus text-info-main px-16 py-4 radius-4 fw-medium text-xs">
                {row.type || "N/A"}
              </span>
            ) 
          },
          { 
            key: "parent_main_name", 
            label: "Parent Business", 
            className: "text-secondary-light fw-medium",
            sortable: true 
          },
        ]}

        actions={[
          {
            icon: "lucide:edit",
            className: "w-32-px h-32-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: (row) => navigate(`/locations-sub/${row.id}/edit`),
          },
          {
            icon: "mingcute:delete-2-line",
            className: "w-32-px h-32-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: (row) => {
              // REPLACED window.confirm with confirmDelete helper
              confirmDelete(row.name, async () => {
                try {
                  await api.delete(`/sub-locations/${row.id}`);
                  showToast("success", "Sub location deleted"); // UPDATED
                  fetchData();
                } catch {
                  showToast("error", "Delete operation failed"); // UPDATED
                }
              });
            },
          },
        ]}
      />
    </>
  );
};

export default LocationsSubListPage;